import { renderMeasure, renderPrice, renderWeight } from '../../utils';
import {
  IsSort,
  Table,
  TableBody,
  TableCell,
  TableCellHead,
  TableHead,
  TableRow,
} from '~/ui/Table';
import { IconButton, InputBar } from '~/ui';
import Add from '~/assets/svg/newSvg/add.svg?react';
import Remove from '~/assets/svg/newSvg/remove.svg?react';
import cn from 'classnames';
import styles from './FullMenuTable.module.scss';
import { useTranslation } from 'react-i18next';
import { ISelectedWares } from './FullMenuBoard';
import { floatRegex, integerRegex, wordRegex } from '~/utils/regex/regex';
import { useState } from 'react';

interface MenuItem {
  id: number;
  wares_name: string;
  price_list: { price: string; price_list_name: string };
  parent: string;
  weight: string;
  measure: string;
  position: number;
  currency: string;
}

interface Props {
  menu: MenuItem[];
  sort: IsSort;
  setSort: ({ field, direction }: IsSort) => void;
  selectedWares: ISelectedWares[];
  setSelectedWares: React.Dispatch<React.SetStateAction<ISelectedWares[]>>;
}

export const FullMenuTable = ({
  menu,
  sort,
  setSort,
  selectedWares,
  setSelectedWares,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [positionNumber, setPositionNumber] = useState('');
  const isWaresSelected = (id: number) => {
    return selectedWares.some(ware => ware.id === id);
  };

  const handleDishPrice = (id: number, newPrice: string) => {
    if (floatRegex.test(newPrice)) {
      setSelectedWares(prev =>
        prev.map(ware => (ware.id === id ? { ...ware, price: newPrice } : ware))
      );
    }
  };

  const handleDishQuantity = (id: number, newQuantity: string) => {
    if (newQuantity === '' || integerRegex.test(newQuantity)) {
      setSelectedWares(prev =>
        prev.map(ware =>
          ware.id === id ? { ...ware, max_amount: newQuantity } : ware
        )
      );
    }
  };

  const handleDishWeight = (id: number, newQuantity: string) => {
    if (newQuantity === '' || integerRegex.test(newQuantity)) {
      setSelectedWares(prev =>
        prev.map(ware =>
          ware.id === id ? { ...ware, weight: newQuantity } : ware
        )
      );
    }
  };

  const handleDishMeasure = (id: number, newQuantity: string) => {
    if (newQuantity === '' || wordRegex.test(newQuantity)) {
      setSelectedWares(prev =>
        prev.map(ware =>
          ware.id === id ? { ...ware, measure: newQuantity } : ware
        )
      );
    }
  };

  const handleDishPosition = (id: number, newPosition: string) => {
    if (newPosition === '' || integerRegex.test(newPosition)) {
      setSelectedWares(prev =>
        prev.map(ware =>
          ware.id === id ? { ...ware, position: newPosition } : ware
        )
      );
    }
  };

  const getEditedPrice = (id: number, fallback: string) => {
    const ware = selectedWares.find(ware => ware.id === id);
    return ware?.price ?? fallback;
  };

  const getEditedQuantity = (id: number, fallback: string) => {
    const ware = selectedWares.find(ware => ware.id === id);
    return ware?.max_amount ?? fallback;
  };

  const getEditedWeight = (id: number, fallback: string) => {
    const ware = selectedWares.find(ware => ware.id === id);
    return ware?.weight ?? fallback;
  };

  const getEditedMeasure = (id: number, fallback: string) => {
    const ware = selectedWares.find(ware => ware.id === id);
    return ware?.measure ?? fallback;
  };

  const getEditedPosition = (id: number, fallback: string) => {
    const ware = selectedWares.find(ware => ware.id === id);
    return ware?.position ?? fallback;
  };

  return (
    <Table>
      <TableHead sort={sort} setSort={setSort}>
        {selectedWares.length > 0 && (
          <TableCellHead field="wares_name">{t('position')}</TableCellHead>
        )}
        <TableCellHead field="wares_name">{t('name')}</TableCellHead>
        <TableCellHead sortable={false} field="parent">
          {t('category')}
        </TableCellHead>
        <TableCellHead field="price">{t('price')}</TableCellHead>
        <TableCellHead field="weight" className={styles.weight}>
          {t('measure')}
        </TableCellHead>
        <TableCellHead field="quantity" sortable={false}>
          {t('quantity_full')}
        </TableCellHead>
        <TableCellHead sortable={false} field="actions">
          {t('action')}
        </TableCellHead>
      </TableHead>
      <TableBody>
        {menu.map(
          ({
            id,
            wares_name,
            parent,
            price_list,
            weight,
            measure,
            position,
            currency,
          }: MenuItem) => (
            <TableRow key={id}>
              {selectedWares.length > 0 &&
                (selectedWares.some(ware => ware.id === id) ? (
                  <TableCell>
                    <InputBar
                      value={getEditedPosition(id, positionNumber ?? '')}
                      name="position"
                      onChange={e => handleDishPosition(id, e.target.value)}
                    />
                  </TableCell>
                ) : (
                  <TableCell>{position}</TableCell>
                ))}
              <TableCell>{wares_name}</TableCell>
              <TableCell>{parent}</TableCell>
              {selectedWares.some(ware => ware.id === id) ? (
                <TableCell>
                  <InputBar
                    value={getEditedPrice(id, price_list?.price ?? '')}
                    name="dishPrice"
                    onChange={e => {
                      handleDishPrice(id, e.target.value);
                    }}
                  />
                </TableCell>
              ) : (
                <TableCell>
                  {renderPrice(price_list?.price, currency)}
                </TableCell>
              )}
              {selectedWares.some(ware => ware.id === id) ? (
                <TableCell className={styles.measureWrapper}>
                  <InputBar
                    name="weight"
                    value={getEditedWeight(id, weight ?? '')}
                    onChange={e => handleDishWeight(id, e.target.value)}
                  />
                  <InputBar
                    name="measure"
                    value={getEditedMeasure(id, measure ?? '')}
                    onChange={e => handleDishMeasure(id, e.target.value)}
                  />
                </TableCell>
              ) : (
                <TableCell>
                  {renderMeasure(weight)} {measure}
                </TableCell>
              )}
              {selectedWares.some(ware => ware.id === id) ? (
                <TableCell>
                  <InputBar
                    name="quantity"
                    value={getEditedQuantity(id, '')}
                    onChange={e => {
                      handleDishQuantity(id, e.target.value);
                    }}
                  />
                </TableCell>
              ) : (
                <TableCell>{0}</TableCell>
              )}
              <TableCell>
                <div className={styles.actions}>
                  <IconButton
                    onClick={() => {
                      const newReight = renderWeight(weight);
                      const newWares = [
                        ...selectedWares,
                        {
                          id,
                          price: price_list?.price,
                          max_amount: '',
                          weight: newReight,
                          measure,
                          position: positionNumber,
                        },
                      ];
                      setSelectedWares(newWares);
                      setPositionNumber('');
                    }}
                    className={cn(styles.btnLink, {
                      [styles.addVisible]: isWaresSelected(id),
                      [styles.addInvisible]: !isWaresSelected(id),
                    })}
                  >
                    <Add />
                  </IconButton>
                  <IconButton
                    className={cn(styles.btnLink, {
                      [styles.removeVisible]: isWaresSelected(id),
                      [styles.removeInvisible]: !isWaresSelected(id),
                    })}
                    onClick={() => {
                      const filteredWares = selectedWares.filter(
                        el => el.id !== id
                      );
                      setSelectedWares(filteredWares);
                    }}
                  >
                    <Remove />
                  </IconButton>
                </div>
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </Table>
  );
};
